import React, { useEffect, useState, useMemo, useRef } from "react";
import SearchBar from "../Main/searchBar";
import { ReactComponent as BackIcon } from "../../Assets/Categories/BackIcon.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  editBookingAvailable,
  getProjectData,
  getProjectLocalities,
  updateProjectLocalities,
} from "../../Redux/Actions/projectActions";
import axios from "axios";
import "./viewProjects.css";
import { sendNotification } from "../../Redux/Actions/notificationActions";
import { toast } from "react-hot-toast";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { viewCollectionsList } from "../../Redux/Actions/collectionsActions";
import { viewTeamMembersListNoPagination } from "../../Redux/Actions/teamMembersAction";
import { getEnums } from "../../Redux/Actions/enumsActions";
import DeclineModal from "../../Global Functions/declineModal";
import ListShowerModal from "../../Global Functions/listShower";
import { DateToLocale, ToFirstCapital } from "../../Utils/helpers";
import SelectInput from "../../Global Functions/selectInput";
import { addStatus } from "../../Redux/Actions/consultantAction";

const ViewProjects = () => {
  const { id: project_id } = useParams();
  const location = useLocation();
  const fromPage = location.state?.from;
  const consultant_id = location.state?.consultant_id;
  const FLAG_URL = process.env.REACT_APP_API_FLAG_URL;
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [projectState, setProjectState] = useState({});
  const [selectedCollections, setSelectedCollections] = useState("");
  const [isPopupVisible, setIsPopupVisible] = useState(false);

  const [selectedCollectionForSub, setSelectedCollectionForSub] = useState("");
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [selectedSubCollections, setSelectedSubCollections] = useState([]);

  const [reasonPopup, setReasonPopUp] = useState(false);
  const [declinedReason, setDeclinedReason] = useState("");
  const [amenityPopup, setAmenityPopup] = useState(false);
  const [status, setStatus] = useState("");
  const [bookingAvailable, setBookingAvailable] = useState(false);
  const isTogglingBooking = useRef(false);
  const selectedExecutiveId = projectState?.payment_executive?._id || "";

  // ── Localities State ──
  const [localitiesPopup, setLocalitiesPopup] = useState(false);
  const [selectedLocalityIds, setSelectedLocalityIds] = useState([]);
  const [localitySearch, setLocalitySearch] = useState("");

  const dispatch = useDispatch();

  const { enumsData } = useSelector((state) => state.enumsData);
  const { teamMembers } = useSelector((state) => state.teamMembers);
  const { collections } = useSelector((state) => state.collections);
  const { subCollections } = useSelector((state) => state.collections);
  const { projectLocalities, localitiesLoading, updateLocalitiesLoading } =
    useSelector((state) => state.projects);

  const collectionPayload = {
    page: 1,
    limit: 100,
  };

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  });

  const teamData = { role: "payment_executive" };

  useEffect(() => {
    dispatch(getEnums());
    dispatch(viewCollectionsList(collectionPayload));
    dispatch(viewTeamMembersListNoPagination(teamData));
    dispatch(getProjectData(project_id));
  }, [dispatch, project_id]);

  useEffect(() => {
    if (selectedCollectionForSub) {
      dispatch(
        viewCollectionsList({
          page: 1,
          limit: 100,
          collection_id: selectedCollectionForSub,
        })
      );
    }
  }, [selectedCollectionForSub, dispatch]);

  // ── Fetch localities when popup opens ──
  useEffect(() => {
    if (localitiesPopup) {
      dispatch(getProjectLocalities({ project_id }));
    }
  }, [localitiesPopup, dispatch, project_id]);

  const { project } = useSelector((state) => state.projects);

  const enums = enumsData?.data?.ProjectStatus || {};

  const { admin } = useSelector((state) => state.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canAdd = Array.isArray(accessCode) && accessCode.includes('add');
  const canEdit = Array.isArray(accessCode) && accessCode.includes('edit');


  useEffect(() => {
    const data = project?.data || {};
    setProjectState(data);
    if (!isTogglingBooking.current) {
      setBookingAvailable(data.is_booking_available ?? false);
    }
  }, [project]);

  // ── Sync selected localities from project data ──
  useEffect(() => {
    const projectLocalitiesData = project?.data?.localities;
    if (Array.isArray(projectLocalitiesData)) {
      const preSelected = projectLocalitiesData.map((loc) => loc._id);
      setSelectedLocalityIds(preSelected);
    }
  }, [project]);

  const localitiesList = Array.isArray(projectLocalities?.data) ? projectLocalities.data : [];

  const baseOrder = useMemo(() => {
    if (!localitiesPopup) return [];
    return [...localitiesList].sort((a, b) => {
      const aSelected = selectedLocalityIds.includes(a._id) ? 0 : 1;
      const bSelected = selectedLocalityIds.includes(b._id) ? 0 : 1;
      return aSelected - bSelected;
    });
  }, [localitiesPopup, localitiesList]); 

  const filteredLocalities = baseOrder.filter((loc) =>
    loc.name?.toLowerCase().includes(localitySearch.toLowerCase()) ||
    loc.city_data?.city_name?.toLowerCase().includes(localitySearch.toLowerCase())
  );

  const toggleLocalitySelection = (id) => {
    setSelectedLocalityIds((prev) =>
      prev.includes(id) ? prev.filter((lid) => lid !== id) : [...prev, id]
    );
  };

  const handleSaveLocalities = async () => {
    if (selectedLocalityIds.length === 0) {
      return toast.error("Please select at least one locality");
    }
    const result = await dispatch(
      updateProjectLocalities({
        id: project_id,
        localities: selectedLocalityIds,
      })
    );
    if (result) {
      setLocalitiesPopup(false);
      setLocalitySearch("");
    }
  };

  const bookingAvailableToggleHandler = async () => {
    const newValue = !bookingAvailable;
    isTogglingBooking.current = true;
    setBookingAvailable(newValue);
    try {
      await dispatch(
        editBookingAvailable({
          project_id: project_id,
          is_booking_available: newValue,
        })
      );
      toast.success(
        newValue ? "Booking enabled successfully!" : "Booking disabled successfully!"
      );
    } catch (error) {
      setBookingAvailable(!newValue);
      toast.error("Failed to update booking availability");
      console.error("Error updating booking availability:", error);
    } finally {
      isTogglingBooking.current = false;
    }
  };

  const paymentExecutiveHandler = async (event) => {
    const executive_id = event.target.value;
    try {
      await axios
        .post(
          `${BASE_URL}/project/add-payment-executive`,
          {
            project_id: project_id,
            payment_executive_id: executive_id,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
        .then(() => {
          toast.success("Executive Set Successfully");
          dispatch(getProjectData(project_id));
        })
        .finally(() => console.log("Executive Set"));
    } catch (error) {
      console.error("Problem in adding executive", error.message);
    }
  };

  const StatusChangeHandler = async (event) => {
    let updatedStatus = event.target.value;
    const currentStatus = projectState.status;
    if (
      updatedStatus === "declined" &&
      !["requested", "approved"].includes(projectState.status)
    ) {
      return toast.error(
        'You can only select "Declined" status when current status is Requested or Approved'
      );
    }

    if (
      ["published", "unpublished", "sold"].includes(currentStatus) &&
      ["requested", "declined", "approved"].includes(updatedStatus)
    ) {
      return toast.error(
        `Cannot change status to "${updatedStatus}" when the current status is "${currentStatus}"`
      );
    }

    if (updatedStatus === "requested" && projectState.status !== "published") {
      return toast.error("First you have to publish the project");
    }
    if (
      updatedStatus === "sold" &&
      projectState.status !== "published" &&
      projectState.status !== "unpublished"
    ) {
      return toast.error("You must publish the project before marking as sold");
    }

    let statusPayload = {
      project_id: project_id,
      status: updatedStatus,
    };

    if (
      (updatedStatus === "published" || updatedStatus === "unpublished") &&
      (!projectState.payment_executive ||
        (Array.isArray(projectState.payment_executive) &&
          projectState.payment_executive.length === 0) ||
        (typeof projectState.payment_executive === "object" &&
          !projectState.payment_executive._id))
    ) {
      return toast.error(
        "Please select a payment executive before changing to published or unpublished status"
      );
    }

    if (updatedStatus === "declined") {
      setReasonPopUp(!reasonPopup);
      return;
    }

    try {
      await axios
        .post(`${BASE_URL}/project/change-status`, statusPayload, {
          headers: {
            "Content-Type": "application/json",
          },
        })
        .then(async () => {
          toast.success("Project Published Successfully!");

          if (updatedStatus === "published") {
            const selectedProject = projectState || {};
            const collections = selectedProject.collections || [];
            const isInvestment = selectedProject.is_investment_opportunity || false;

            const baseMessage = `New project "${selectedProject.project_name}" is now live and open for booking!`;
            let finalMessage = baseMessage;
            let suffixes = [];

            if (collections.some((col) => col?.name_withoutspace === "holidayhomes")) {
              suffixes.push("Perfect for **Holiday Homes** – ideal vacation investment");
            }

            if (isInvestment) {
              suffixes.push("Exclusive **Investment Opportunity**");
            }

            if (suffixes.length > 0) {
              finalMessage = `${baseMessage} ${suffixes.join(" | ")}`;
            }

            try {
              await dispatch(
                sendNotification({
                  title: "Project Published",
                  message: finalMessage,
                  type_name: "project",
                  type_id: selectedProject._id,
                  type: "all",
                  notification_for: "user",
                  list: [],
                  image: selectedProject.cover_photo || "",
                  type_data: {
                    project_id: selectedProject.project_id,
                    project_name: selectedProject.project_name,
                    brand_name: selectedProject.brand_name,
                    construction_status: selectedProject.construction_status,
                    is_investment_opportunity: isInvestment,
                    collections: collections,
                    cover_photo: selectedProject.cover_photo,
                  },
                })
              );
            } catch (err) {
              console.error("User notification failed:", err);
            }

            try {
              await dispatch(
                sendNotification({
                  title: "Project Published",
                  message: finalMessage,
                  notification_for: "builder",
                  type: "selected",
                  list: [selectedProject.builder],
                  image: selectedProject.cover_photo || "",
                  type_name: "project",
                  type_id: selectedProject._id,
                  type_data: {
                    project_id: selectedProject.project_id,
                    project_name: selectedProject.project_name,
                    brand_name: selectedProject.brand_name,
                    construction_status: selectedProject.construction_status,
                    is_investment_opportunity: isInvestment,
                  },
                })
              );
            } catch (err) {
              console.error("Builder notification failed:", err);
            }
          }

          dispatch(getProjectData(project_id));
        })
        .catch((error) => console.error("Error Changing Status:", error));
    } catch (error) {
      console.error("Error Changing Status:", error?.data?.response?.message);
    }
  };

  const DeclineChangeHandler = async () => {
    const updatedStatus = "declined";

    if (!declinedReason || declinedReason === "") {
      return toast.error("Please specify the reason for declining!");
    }

    const statusPayload = {
      project_id: project_id,
      status: updatedStatus,
      declined_reason: declinedReason,
    };

    try {
      await axios.post(`${BASE_URL}/project/change-status`, statusPayload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      toast.success("Status Changed Successfully");
      setReasonPopUp(false);
    } catch (error) {
      console.error("Error Changing Status:", error);
    }
  };

  const collectionSelectHandler = (event) => {
    const selectedOption = event.target.value;
    if (!selectedOption) return;

    const existingCollectionIds = Array.isArray(projectState.collections)
      ? projectState.collections.map((c) => c._id)
      : [];

    if (existingCollectionIds.includes(selectedOption)) {
      return toast.error("Already Exists!!");
    }

    setSelectedCollections(selectedOption);
    setSelectedCollectionForSub(selectedOption);
    setSelectedSubCollections([]);
  };

  const handleRemoveCollection = async (collection) => {
    try {
      const updatedCollections = projectState?.collections
        ?.filter((c) => c._id !== collection._id)
        .map((c) => c._id);

      await axios.post(
        `${BASE_URL}/project/update-collection`,
        {
          project_id: project_id,
          collection: updatedCollections,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("Removed from Collections!");
      dispatch(getProjectData(project_id));
      setSelectedCollections((prev) => (prev === collection._id ? "" : prev));
    } catch (error) {
      console.error("Error removing collection:", error);
      toast.error("Failed to remove collection.");
    }
  };

  const handleCollectionAddition = async () => {
    if (!selectedCollections) {
      return toast.error("Please select a collection to add");
    }
    try {
      const existingCollectionIds = Array.isArray(projectState.collections)
        ? projectState.collections.map((c) => c._id)
        : [];

      const combinedCollectionIds = [
        ...existingCollectionIds,
        selectedCollections,
        ...selectedSubCollections,
      ];

      const uniqueCollectionIds = Array.from(new Set(combinedCollectionIds));

      await axios.post(
        `${BASE_URL}/project/update-collection`,
        {
          project_id: project_id,
          collection: uniqueCollectionIds,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("Added to Collections!");
      dispatch(getProjectData(project_id));
      setSelectedCollections("");
      setSelectedSubCollections([]);
      setSelectedCollectionForSub("");
      setIsPopupVisible(false);
    } catch (error) {
      console.log("Problem Adding", error);
      toast.error("Failed to add collections.");
    }
  };

  const statusHandler = (e) => {
    setStatus(e.target.value);
    const statusPayload = {
      consultant_id: consultant_id,
      project_id: project_id,
      status: e.target.value,
    };
    dispatch(addStatus(statusPayload));
  };

  const navigate = useNavigate();

  const togglePopup = () => {
    setIsPopupVisible(!isPopupVisible);
    setSelectedCollections("");
    setSelectedCollectionForSub("");
    setSelectedSubCollections([]);
  };

  const amenityPopupHandler = () => {
    setAmenityPopup(!amenityPopup);
  };

  const quickStats = useMemo(
    () => [
      {
        icon: "🏗️",
        label: "Construction Status",
        value: projectState?.construction_status || "N/A",
      },
      {
        icon: "👁️",
        label: "VR Visits",
        value: projectState?.vr_view_count || "N/A",
      },
      {
        icon: "📅",
        label: "Launch Date",
        value: projectState?.launch_date?.slice(0, 10) || "N/A",
      },
      {
        icon: "📦",
        label: "Total Bookings",
        value: projectState?.booking_count ?? "N/A",
      },
      {
        icon: "📏",
        label: "Project Area",
        value: `${projectState?.project_area || "N/A"} ${projectState?.area_unit || ""
          }`,
      },
    ],
    [projectState]
  );

  return (
    <div
      className="grid-container padding-20 gap-10"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gridAutoRows: "minmax(60px, auto)",
        background: "#F5F7FA",
      }}
    >
      <div style={{ gridColumn: "4/8" }}>
        <SearchBar style={{ width: "max-content" }} />
      </div>

      <div
        className="back-icon margin-0 gap-10 align-center flex-container"
        style={{ gridColumn: "span 7" }}
      >
        <div onClick={() => navigate(-1)}>
          <BackIcon style={{ cursor: "pointer" }} />
        </div>
        <div>
          <h3>{projectState?.project_name || "N/A"}</h3>
          <h4 className="breadcrumb-subtitle">Projects / List / View</h4>
        </div>
      </div>

      <div className="modern-property-view">
        {/* Hero Banner */}
        <div className="hero-banner">
          <div className="hero-image-container" style={{ height: "250px" }}>
            <img
              src={projectState?.cover_photo || ""}
              alt="Project"
              className="hero-main-image"
              loading="eager"
              fetchpriority="high"
              width="1200"
              height="600"
            />
            <div className="hero-overlay">
              <div className="hero-badges">
                <span className="status-pill construction">
                  {projectState?.construction_status || "N/A"}
                </span>
                <span className="status-pill featured">Featured</span>
              </div>
            </div>
          </div>

          <div className="hero-content-card">
            <div className="project-header">
              <div>
                <h1 className="project-name">
                  {projectState?.project_name || "N/A"}
                </h1>
                <p className="project-meta">
                  <span>📍</span> {projectState?.brand_name || "N/A"} • ID:{" "}
                  {projectState?.project_id || "N/A"}
                </p>
              </div>
              <div>
                <span
                  className="price-label"
                  style={{ fontSize: "18x", marginRight: "25px" }}
                >
                  Status{" "}
                </span>
                <div className="price-tag-large">
                  <span className="price-value" style={{ fontSize: "16px" }}>
                    {ToFirstCapital(projectState?.status || "N/A")}
                  </span>
                </div>
              </div>
            </div>

            <div
              className="quick-stats"
              style={{ gridTemplateColumns: "repeat(5, 1fr)" }}
            >
              {quickStats.map((stat, index) => (
                <div key={index} className="quick-stat-item">
                  <span className="stat-icon">{stat.icon}</span>
                  <div>
                    <span className="stat-number">{stat.value}</span>
                    <span className="stat-label">{stat.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {canEdit &&
          <div className="status-selector-container booking-toggle-section">
            <label className="selector-label booking-toggle-heading">
              Booking Availability
            </label>

            <div className="booking-toggle-row">
              <div className="booking-toggle-info">
                <p className="booking-toggle-label">
                  {bookingAvailable ? "Booking Open" : "Booking Closed"}
                </p>
                <p className="booking-toggle-desc">
                  {bookingAvailable
                    ? "Users can currently book this project"
                    : "Bookings are currently disabled for this project from admin"}
                </p>
              </div>

              <div className="booking-toggle-right">
                <div
                  className={`booking-switch ${bookingAvailable ? "booking-switch--on" : ""
                    }`}
                  onClick={bookingAvailableToggleHandler}
                  role="switch"
                  aria-checked={bookingAvailable}
                >
                  <div className="booking-switch-thumb" />
                </div>
                <span
                  className={`booking-badge ${bookingAvailable ? "badge-active" : "badge-inactive"
                    }`}
                >
                  <span className="badge-dot" />
                  {bookingAvailable ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>
        }

        {/* Status & Executive Controls */}
        {canAdd &&
          <div
            className="status-selector-container"
            style={{ background: "white" }}
          >
            <label
              className="selector-label"
              style={{ color: "black", fontSize: "20px", fontWeight: "bolder" }}
            >
              {fromPage
                ? "Consultant Status"
                : "Project Status & Payment Executive"}
            </label>

            {fromPage ? (
              <div className="consultant-status-wrapper">
                <SelectInput
                  value={status}
                  onChange={statusHandler}
                  placeholder="Select an Option"
                  options={[
                    { label: "Approved", value: "approved" },
                    { label: "Declined", value: "declined" },
                  ]}
                />
              </div>
            ) : (
              <div className="admin-controls-grid">
                <div className="control-item">
                  <span className="info-key" style={{ marginLeft: "13px" }}>
                    Payment Executive
                  </span>
                  <SelectInput
                    value={selectedExecutiveId}
                    onChange={paymentExecutiveHandler}
                    placeholder="Select Payment Executive"
                    options={
                      Array.isArray(teamMembers?.data)
                        ? teamMembers.data.map((member) => ({
                          label: member.name,
                          value: member._id,
                        }))
                        : []
                    }
                  />
                </div>

                <div className="control-item">
                  <span className="info-key">Project Status</span>
                  <select
                    value={projectState?.status || ""}
                    onChange={StatusChangeHandler}
                    className="status-selects"
                    style={{ marginTop: "13px" }}
                  >
                    <option value="" disabled>
                      Select Status
                    </option>
                    {Object.entries(enums).map(([key, value]) => (
                      <option key={key} value={value}>
                        {ToFirstCapital(value)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        }
        {/* About Project */}
        <div
          className="info-section-card full-width about-section"
          style={{
            marginBottom: "38px",
            background: "white",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <h3
            className="section-title"
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#1a202c",
              marginBottom: "16px",
            }}
          >
            About this Project
          </h3>

          <div style={{ position: "relative" }}>
            <div
              style={{
                maxHeight: aboutExpanded ? "none" : "80px",
                overflow: "hidden",
                transition: "max-height 0.3s ease",
                lineHeight: "1.6",
                color: "black",
                fontWeight: "600",
                fontSize: "14px",
                position: "relative",
              }}
            >
              {projectState?.about_project || "No description available."}
            </div>

            {!aboutExpanded &&
              projectState?.about_project &&
              projectState.about_project.length > 150 && (
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "40px",
                    background:
                      "linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))",
                    pointerEvents: "none",
                  }}
                />
              )}
          </div>

          {projectState?.about_project &&
            projectState.about_project.length > 150 && (
              <button
                onClick={() => setAboutExpanded(!aboutExpanded)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#3182ce",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  marginTop: "12px",
                  padding: "0",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.textDecoration = "underline")
                }
                onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
              >
                {aboutExpanded ? "Show less" : "Read more"}
                <span style={{ fontSize: "10px" }}>
                  {aboutExpanded ? "▲" : "▼"}
                </span>
              </button>
            )}

          {projectState?.landmarks?.length > 0 && (
            <div
              style={{
                marginTop: "24px",
                paddingTop: "24px",
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <h4
                style={{
                  fontSize: "15px",
                  fontWeight: "600",
                  color: "#2d3748",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span style={{ fontSize: "16px" }}>📍</span>
                Nearby Landmarks
              </h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {projectState.landmarks.map((landmark, index) => (
                  <span
                    key={index}
                    style={{
                      padding: "6px 12px",
                      background: "#f7fafc",
                      color: "black",
                      borderRadius: "6px",
                      fontWeight: "600",
                      fontSize: "13px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    {landmark}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="tab-content">
          <div className="info-grid-modern">
            <div className="info-section-card">
              <h3 className="section-title">Basic Information</h3>
              <div className="info-rows">
                <div className="info-row">
                  <span className="info-key">RERA ID</span>
                  <span
                    className="info-values"
                    style={{ background: "transparent" }}
                  >
                    {projectState?.rera_id || "N/A"}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-key">Project Type</span>
                  <span
                    className="info-values"
                    style={{ background: "transparent" }}
                  >
                    {projectState?.project_type?.name || "N/A"}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-key">Age of Project</span>
                  <span
                    className="info-values"
                    style={{ background: "transparent" }}
                  >
                    {projectState?.property_age || "N/A"}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-key">Upload Date</span>
                  <span
                    className="info-values"
                    style={{ background: "transparent" }}
                  >
                    {DateToLocale(projectState?.created_at)}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-section-card">
              <h3 className="section-title">Location</h3>
              <div className="info-rows">
                <div className="info-row">
                  <span className="info-key">Country</span>
                  <span
                    className="info-values country-with-flag"
                    style={{ background: "transparent" }}
                  >
                    <img
                      src={`${FLAG_URL}${projectState?.country?.country_flag}`}
                      alt="Country flag"
                      className="country-flag"
                    />
                    {projectState?.country?.country_name || "N/A"}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-key">State</span>
                  <span
                    className="info-values"
                    style={{ background: "transparent" }}
                  >
                    {projectState?.state?.state_name || "N/A"}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-key">City</span>
                  <span
                    className="info-values"
                    style={{ background: "transparent" }}
                  >
                    {projectState?.city?.city_name || "N/A"}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-key">Street Address</span>
                  <span
                    className="info-values"
                    style={{ background: "transparent" }}
                  >
                    {projectState?.street_address || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="features-section">
            <div className="feature-card accent-blue">
              <h4 className="feature-title">Collections</h4>
              <div className="feature-tags collections-tags">
                {Array.isArray(projectState?.collections) &&
                  projectState.collections.length > 0 ? (
                  projectState.collections.map((collection) => (
                    <div
                      key={collection._id}
                      className="feature-tag blue collection-chip"
                      style={{ fontWeight: "600", color: "#273e57" }}
                    >
                      {collection.name}
                      <button
                        className="remove-chip-btn"
                        onClick={() => handleRemoveCollection(collection)}
                        aria-label={`Remove ${collection.name}`}
                      >
                        ×
                      </button>
                    </div>
                  ))
                ) : (
                  <span className="no-data-text">No collections added</span>
                )}
              </div>

              <div
                className="action-card-modern add-collection-trigger"
                style={{
                  background: "transparent",
                  border: "2px solid rgb(0 0 0 / 26%)",
                }}
                onClick={togglePopup}
              >
                <span className="action-icon-modern">✨</span>
                <div className="action-content">
                  <h4 style={{ color: "black", fontSize: "18px" }}>
                    Add Collection
                  </h4>
                  <p>Click to add new collections</p>
                </div>
                <span className="action-arrow">→</span>
              </div>
            </div>

            <div className="feature-card accent-green">
              <h4 className="feature-title">Amenities</h4>
              <div className="feature-tags">
                <span className="feature-tag green">
                  {projectState?.amenities?.length || 0} Amenities
                </span>
              </div>
              <div
                className="action-card-modern"
                style={{
                  top: "27px",
                  background: "transparent",
                  border: "2px solid rgb(0 0 0 / 26%)",
                }}
                onClick={amenityPopupHandler}
              >
                <span className="action-icon-modern">✨</span>
                <div className="action-content">
                  <h4 style={{ color: "black", fontSize: "18px" }}>
                    View Amenities
                  </h4>
                  <p>Click to see full list</p>
                </div>
                <span className="action-arrow">→</span>
              </div>
            </div>

            {/* ── Localities Feature Card ── */}
            <div className="feature-card accent-purple">
              <h4 className="feature-title">Localities</h4>
              <div className="feature-tags localities-tags">
                {selectedLocalityIds.length > 0 ? (
                  <span className="feature-tag locality-chip">
                    {selectedLocalityIds.length} Localit
                    {selectedLocalityIds.length === 1 ? "y" : "ies"} Selected
                  </span>
                ) : (
                  <span className="no-data-text">No localities selected</span>
                )}
              </div>
              <div
                className="action-card-modern"
                style={{
                  top: "12px",
                  background: "transparent",
                  border: "2px solid rgb(0 0 0 / 26%)",
                }}
                onClick={() => setLocalitiesPopup(true)}
              >
                <span className="action-icon-modern">📍</span>
                <div className="action-content">
                  <h4 style={{ color: "black", fontSize: "18px" }}>
                    Manage Localities
                  </h4>
                  <p>Click to select nearby localities</p>
                </div>
                <span className="action-arrow">→</span>
              </div>
            </div>

          </div>
          <div className="info-section-card full-width map-section">
            <h3 className="section-title">Project Location</h3>
            <div className="map-container">
              {isLoaded && projectState?.location?.coordinates ? (
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                  zoom={15}
                  center={{
                    lat: projectState.location.coordinates[1],
                    lng: projectState.location.coordinates[0],
                  }}
                >
                  <Marker
                    position={{
                      lat: projectState.location.coordinates[1],
                      lng: projectState.location.coordinates[0],
                    }}
                  />
                </GoogleMap>
              ) : (
                <div className="map-placeholder">Loading map...</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Collections Popup */}
      {isPopupVisible && (
        <div
          className="collection-popup-overlay"
          onClick={() => setIsPopupVisible(false)}
        >
          <div
            className="collection-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <h3 className="popup-title">Add to Collections</h3>
              <button
                className="close-btn"
                onClick={() => setIsPopupVisible(false)}
                aria-label="Close"
                style={{
                  background: "transparent",
                  color: "black",
                  marginBottom: "25px",
                  fontSize: "20px",
                  padding: "0",
                  // color: "black"
                }}
              >
                ✕
              </button>
            </div>

            <select
              onChange={collectionSelectHandler}
              value={selectedCollectionForSub}
              className="collection-main-select"
            >
              <option value="">Select Collection</option>
              {Array.isArray(collections?.data) &&
                collections.data
                  .filter(
                    (c) =>
                      !projectState?.collections?.some(
                        (ex) => ex._id === c._id
                      )
                  )
                  .map((collection) => (
                    <option key={collection._id} value={collection._id}>
                      {collection.name}
                    </option>
                  ))}
            </select>

            {selectedCollectionForSub && (
              <div className="sub-collections-wrapper">
                <h4 className="sub-title">Sub-Collections</h4>
                <SelectInput
                  multiSelect={true}
                  name="subCollections"
                  value={selectedSubCollections}
                  onChange={(e) => setSelectedSubCollections(e.target.value)}
                  options={
                    Array.isArray(subCollections?.data)
                      ? subCollections.data
                        .filter(
                          (sub) =>
                            !projectState?.collections?.some(
                              (c) => c._id === sub._id
                            )
                        )
                        .map((sub) => ({
                          label: sub.name,
                          value: sub._id,
                        }))
                      : []
                  }
                  placeholder="Select Sub-Collections"
                  filled={true}
                />
              </div>
            )}
            {canAdd &&
              <button
                onClick={handleCollectionAddition}
                className="add-collections-btn"
              >
                Add Collections
              </button>
            }
          </div>
        </div>
      )}

      {/* ── Localities Popup ── */}
      {localitiesPopup && (
        <div
          className="localities-popup-overlay"
          onClick={() => { setLocalitiesPopup(false); setLocalitySearch(""); }}
        >
          <div
            className="localities-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "22px",
                  fontWeight: "600",
                  color: "black",
                }}
              >
                Manage Localities
              </h3>
              <button
                onClick={() => { setLocalitiesPopup(false); setLocalitySearch(""); }}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: "black",
                  padding: "0",
                }}
              >
                ✕
              </button>
            </div>

            {/* Search Input */}
            <div style={{ position: "relative", marginBottom: "14px" }}>
              <span
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "15px",
                  pointerEvents: "none",
                  color: "#9ca3af",
                }}
              >
                🔍
              </span>
              <input
                type="text"
                placeholder="Search by name or city..."
                value={localitySearch}
                onChange={(e) => setLocalitySearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 14px 10px 38px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  fontSize: "14px",
                  color: "#111827",
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              />
              {localitySearch && (
                <button
                  onClick={() => setLocalitySearch("")}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    fontSize: "16px",
                    cursor: "pointer",
                    color: "#9ca3af",
                    padding: "0",
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Selected count badge */}
            <p
              style={{
                fontSize: "13px",
                color: "#6b7280",
                marginBottom: "14px",
              }}
            >
              {selectedLocalityIds.length} localit
              {selectedLocalityIds.length === 1 ? "y" : "ies"} selected
              {localitySearch && (
                <span style={{ marginLeft: "8px", color: "#8b5cf6", fontWeight: "500" }}>
                  • {filteredLocalities.length} result{filteredLocalities.length !== 1 ? "s" : ""} found
                </span>
              )}
            </p>

            {/* Localities List */}
            {localitiesLoading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 0",
                  color: "#6b7280",
                  fontSize: "15px",
                }}
              >
                Loading localities...
              </div>
            ) : localitiesList.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 0",
                  color: "#6b7280",
                  fontSize: "15px",
                }}
              >
                No localities found for this project.
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  maxHeight: "340px",
                  overflowY: "auto",
                  paddingRight: "4px",
                }}
              >
                {filteredLocalities.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "30px 0",
                      color: "#6b7280",
                      fontSize: "14px",
                    }}
                  >
                    No localities match your search.
                  </div>
                ) : (
                  filteredLocalities.map((locality) => {
                    const isSelected = selectedLocalityIds.includes(locality._id);
                    return (
                      <div
                        key={locality._id}
                        onClick={() => toggleLocalitySelection(locality._id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "12px 14px",
                          borderRadius: "10px",
                          border: isSelected
                            ? "2px solid #8b5cf6"
                            : "1px solid #e5e7eb",
                          background: isSelected ? "#f5f3ff" : "#fafafa",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {/* Checkbox */}
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            borderRadius: "5px",
                            border: isSelected
                              ? "2px solid #8b5cf6"
                              : "2px solid #d1d5db",
                            background: isSelected ? "#8b5cf6" : "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            transition: "all 0.15s ease",
                          }}
                        >
                          {isSelected && (
                            <span
                              style={{
                                color: "white",
                                fontSize: "13px",
                                lineHeight: 1,
                              }}
                            >
                              ✓
                            </span>
                          )}
                        </div>

                        {/* Locality Info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p
                            style={{
                              margin: 0,
                              fontWeight: "600",
                              fontSize: "14px",
                              color: "#111827",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {locality.name}
                          </p>
                          <p
                            style={{
                              margin: "2px 0 0",
                              fontSize: "12px",
                              color: "#6b7280",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {locality.city_data?.city_name || ""} •{" "}
                            {locality.short_address}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Save Button */}
            {canAdd &&
              <button
                onClick={handleSaveLocalities}
                disabled={updateLocalitiesLoading}
                style={{
                  marginTop: "24px",
                  width: "100%",
                  padding: "14px",
                  border: "1px solid #8b5cf6",
                  background: updateLocalitiesLoading ? "#e9d5ff" : "white",
                  color: "#6b46c1",
                  borderRadius: "10px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: updateLocalitiesLoading ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!updateLocalitiesLoading) {
                    e.currentTarget.style.background = "#8b5cf6";
                    e.currentTarget.style.color = "white";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!updateLocalitiesLoading) {
                    e.currentTarget.style.background = "white";
                    e.currentTarget.style.color = "#6b46c1";
                  }
                }}
              >
                {updateLocalitiesLoading ? "Saving..." : "Save Localities"}
              </button>
            }
          </div>
        </div>
      )}

      {amenityPopup && (
        <ListShowerModal
          onClick={() => amenityPopupHandler()}
          data={projectState?.amenities}
        />
      )}

      {reasonPopup && (
        <DeclineModal
          onClick={() => setReasonPopUp(!reasonPopup)}
          onSubmit={DeclineChangeHandler}
          onChange={(event) => setDeclinedReason(event.target.value)}
        />
      )}
    </div>
  );
};

export default ViewProjects;